import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertTitle, AlertDescription } from './ui/alert';
import { Checkbox } from './ui/checkbox';
import { Usb, Radio, Cable, Zap, Shield, Battery, CheckCircle, AlertTriangle, BookOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Per-platform flash procedures. Detected from vehicle make/model/ecu_type.
// First matching profile wins; `default` is the fallback.
const FLASH_PROFILES = [
  {
    id: 'bmw_e38_740',
    match: (v) => {
      if (!v) return false;
      const model = (v.model || '').toLowerCase();
      const ecu = (v.ecu_type || '').toLowerCase();
      const chassis = (v.chassis || '').toLowerCase();
      return (
        chassis === 'e38' ||
        model.includes('740') || model.includes('750il') || model.includes('728i') ||
        model.includes('735i') || model.includes('740il') ||
        /\bm5\.2|me7\.2|me7\.8|m3\.3/.test(ecu)
      );
    },
    title: 'BMW 740i / 7-Series E38 Flash Procedure',
    ecu: 'Bosch M5.2 / ME7.2 (chassis E38)',
    tools: ['Galletto 1260 (K-line)', 'MPPS V13+ with K-line cable', 'BMW INPA + NCS Expert / WinKFP (dealer-grade)', 'Autotuner bench (ME7.2 only)'],
    steps: [
      'Confirm chassis: E38 covers 1995–2001 (740i/740iL/750iL). Check VIN 7th digit.',
      'Identify DME: pre-\'98 = M5.2 (K-line only, OBD read/write slow ~20 min). 1999+ = ME7.2 (faster, supports bench).',
      'Battery MUST stay above 12.6V during flash — hook up a shop charger (not jump pack).',
      'Connect OBD-II port under steering column. Ignition to KL-R (position 2, engine OFF).',
      'Galletto/MPPS: Select "BMW > M5.2" or "BMW > ME7.2". READ full binary first — save as your stock backup.',
      'Verify stock file size: M5.2 = 256 KB, ME7.2 = 512 KB or 1 MB (check your ECU part number).',
      'Write the tuned .bin. DO NOT interrupt — K-line writes take 15–30 min on M5.2.',
      'After write: ignition OFF for 30s, then KL-R. Verify DTCs cleared (should show no comm errors).',
      'Clear adaptations: disconnect battery 10 min OR use INPA "LESEN > FS" then "LÖSCHEN".',
      'Test start. Idle for 60s. Monitor AFR — any lean condition = flash back to stock immediately.',
    ],
    warnings: [
      'M5.2 is RSA-unsigned — NO brick protection. One interrupted flash = dead DME. Back up stock TWICE.',
      'ME7.2 on later 740i uses checksum — your tool must correct checksums or the ECU will reject the bin.',
      'If your 740i has the rare "ASC+T" DME variant, K-line speed drops to 10400 baud — session can take 45+ min.',
    ],
    kb_url: 'https://www.e38.org/flashing/',
  },
  {
    id: 'bmw_msv80',
    match: (v) => {
      const ecu = (v?.ecu_type || '').toLowerCase();
      const model = (v?.model || '').toLowerCase();
      return ecu.includes('msv80') || model.includes('328') || model.includes('e90') || model.includes('e91') || model.includes('e92') || model.includes('e93');
    },
    title: 'BMW MSV80 (E90/E91/E92/E93 N51/N52) Flash Procedure',
    ecu: 'Siemens MSV80 / MSV80.1',
    tools: ['BTT MSV80 Quickflash (OBD)', 'Autotuner bench harness', 'KT200 bench'],
    steps: [
      'Battery above 13.5V — hook up a charger.',
      'Plug in OBD adapter (K-DCAN USB or Thor WiFi). Car in KL-R, engine OFF.',
      'Open BTT Quickflash → select FULL FLASH mode (not Cal-only).',
      'READ stock → save as backup with VIN in filename.',
      'WRITE the tuned .bin. Takes ~8 minutes over OBD.',
      'Clear adaptations: disconnect battery 10 min, or use Tool32 "clearadapt" job.',
      'Start car, let idle 2 min, then run a light datalog to verify LTFT is re-learning.',
    ],
    warnings: [
      'MSV80 is signed — wrong checksum = ECU refuses to start. Your tool must resign the binary.',
      'DO NOT modify code area (0x029CDC, 0x029D30) — causes no-start requiring FULL FLASH recovery.',
      'Water pump control at 0x058C00 is untouchable — modifying damages the electric pump.',
    ],
    kb_url: null,
  },
  {
    id: 'gm_e38_e67',
    match: (v) => {
      const ecu = (v?.ecu_type || '').toLowerCase();
      const make = (v?.make || '').toLowerCase();
      return (make === 'gm' || make === 'chevrolet' || make === 'cadillac' || make === 'pontiac') &&
             (ecu.includes('e38') || ecu.includes('e67') || ecu.includes('e40') || ecu.includes('p59') || ecu.includes('p01'));
    },
    title: 'GM LS/Vortec PCM Flash Procedure',
    ecu: 'GM E38 / E40 / E67 / P59 / P01',
    tools: ['HP Tuners MPVI2/MPVI3', 'EFILive FlashScan V3', 'TunerPro RT + LS Droid (for older P59)'],
    steps: [
      'Battery above 13.5V. Shop charger on.',
      'Plug tool into OBD-II. Key to RUN, engine OFF.',
      'READ the full tune (License required — "Read" credit in HP Tuners).',
      'Save stock with VIN in filename.',
      'Open tune, apply changes. Most GM XDFs cover fuel/timing/limiters cleanly.',
      'WRITE the modified tune. Takes ~4 min on E38/E67, ~2 min on E40.',
      'Key OFF 30s. Start. Relearn idle by letting it sit 2 min and then drive normally 15 min.',
    ],
    warnings: [
      'GM PCMs use CVN checksums — your tool must correct them or the ECU throws P0601.',
      'Torque management disable without transmission retune = broken trans.',
      'Newer E98/E99 (2015+) need unlocked service licenses (unlock credits).',
    ],
    kb_url: 'https://github.com/joukoy/gm-checksum-plugins',
  },
  {
    id: 'default',
    match: () => true,
    title: 'Universal ECU Flash Procedure',
    ecu: 'Any ECU',
    tools: ['Match your ECU to one of: OBD tool (Galletto/MPPS/HP Tuners/Autotuner/EcuTek), bench harness (KT200/KESSv2/Dimsport), or boot-mode reader'],
    steps: [
      'Identify your ECU family and read mode (OBD / bench / boot). Check your tool\'s vehicle list.',
      'Battery above 13.5V — connect a shop charger before starting.',
      'READ the full stock binary. Verify size matches the ECU datasheet (do not accept a short read).',
      'Save stock backup with VIN + date in the filename.',
      'WRITE the tuned .bin. Keep the vehicle still, do not open doors (some ECUs drop during BCM wake).',
      'Verify checksum status — if your tool does not auto-correct, run a checksum utility.',
      'Clear adaptations per your ECU family (battery disconnect 10 min is the safe universal).',
      'Idle 60–120 s. Light datalog. Any lean, knock, or stalling → flash stock back immediately.',
    ],
    warnings: [
      'Always have a PRIMARY and a BACKUP copy of your stock file before writing.',
      'Do not write without verifying your tool\'s checksum handling for this ECU family.',
      'If unsure, upload your bin to AI Tuner Pro first — the copilot will tell you exact scaling and safety limits for your ECU.',
    ],
    kb_url: null,
  },
];

function ChecklistItem({ checked, onChange, icon: Icon, label, description }) {
  return (
    <div className={`flex items-start gap-3 p-3 border rounded-sm transition-colors ${
      checked ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-[#27272A] bg-[#050505]'
    }`}>
      <Checkbox checked={checked} onCheckedChange={onChange} className="mt-0.5" />
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 ${checked ? 'text-emerald-400' : 'text-zinc-600'}`} />
        <div>
          <p className={`text-sm ${checked ? 'text-emerald-400' : 'text-white'}`}>{label}</p>
          <p className="text-xs text-zinc-600">{description}</p>
        </div>
      </div>
    </div>
  );
}

export default function FlashECUTab({ vehicle, tunes, selectedTune, onSelectTune }) {
  const [adapter, setAdapter] = useState(null);
  const [checklist, setChecklist] = useState({
    backup: false, battery: false, tune_selected: false, adapter_connected: false
  });
  const [flashing, setFlashing] = useState(false);
  const [flashDone, setFlashDone] = useState(false);

  // Pick the right flash profile for this vehicle.
  const profile = useMemo(() => FLASH_PROFILES.find(p => p.match(vehicle)) || FLASH_PROFILES[FLASH_PROFILES.length - 1], [vehicle]);

  const readyToFlash = Object.values(checklist).every(v => v) && selectedTune && adapter;

  const handleFlash = async () => {
    if (!readyToFlash) return;
    setFlashing(true);
    // Log the flash event
    try {
      const token = localStorage.getItem('auth_token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      await fetch(`${API_URL}/api/flash/log`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          filename: selectedTune.filename,
          flash_type: 'cal_block',
          vehicle_info: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : '',
          notes: `Adapter: ${adapter}`,
        }),
      });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Flash log error:', err);
    }
    // Simulate flash process
    setTimeout(() => {
      setFlashing(false);
      setFlashDone(true);
    }, 2000);
  };

  return (
    <div className="space-y-6" data-testid="flash-ecu-tab">
      <Card className="bg-[#111111] border-[#27272A] rounded-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white font-['Outfit']">
            <Usb className="w-5 h-5 text-red-400" />Flash ECU
          </CardTitle>
          <CardDescription className="text-zinc-400">
            Platform-specific flash guidance for your {vehicle?.year ? `${vehicle.year} ` : ''}{vehicle?.make || ''} {vehicle?.model || ''} ({profile.ecu})
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Step 1: Select Adapter */}
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Step 1: Select Adapter</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                { id: 'thor', name: 'Thor WiFi', desc: 'Wireless OBD2 adapter', icon: Radio, color: 'emerald' },
                { id: 'kdcan', name: 'K-DCAN USB', desc: 'Serial FTDI adapter', icon: Cable, color: 'blue' },
              ].map(a => (
                <button key={a.id} onClick={() => { setAdapter(a.id); setChecklist(p => ({...p, adapter_connected: true})); }}
                  data-testid={`adapter-${a.id}`}
                  className={`p-4 border rounded-sm text-left transition-all ${
                    adapter === a.id ? `border-${a.color}-500/50 bg-${a.color}-500/10` : 'border-[#27272A] bg-[#050505] hover:border-zinc-600'
                  }`}>
                  <div className="flex items-center gap-3">
                    <a.icon className={`w-5 h-5 ${adapter === a.id ? `text-${a.color}-400` : 'text-zinc-500'}`} />
                    <div>
                      <p className="text-sm font-medium text-white">{a.name}</p>
                      <p className="text-xs text-zinc-500">{a.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Step 2: Select Tune */}
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Step 2: Select Tune File</p>
            <select
              value={selectedTune?.filename || ''}
              onChange={e => {
                const tune = tunes.find(t => t.filename === e.target.value);
                onSelectTune(tune);
                setChecklist(p => ({...p, tune_selected: !!tune}));
              }}
              data-testid="flash-tune-select"
              className="w-full bg-[#050505] border border-[#27272A] text-white rounded-sm px-3 py-2.5 text-sm focus:border-[#FF5E1A] outline-none"
            >
              <option value="">Select a tune file...</option>
              {tunes.map(t => {
                const sha = t.sha256 ? ` · sha:${String(t.sha256).slice(0,8)}` : '';
                const ver = t.version ? ` · ${t.version}` : '';
                const dt = t.created_at ? ` · ${new Date(t.created_at).toLocaleDateString()}` : '';
                return <option key={t.filename} value={t.filename}>{t.filename} ({t.size_kb} KB{ver}{sha}{dt})</option>;
              })}
            </select>
          </div>

          {/* Step 3: Pre-flash checklist */}
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Step 3: Pre-Flash Checklist</p>
            <div className="space-y-3">
              <ChecklistItem checked={checklist.backup} onChange={v => setChecklist(p => ({...p, backup: v}))}
                icon={Shield} label="Stock backup saved" description="Ensure you have a backup of your stock tune" />
              <ChecklistItem checked={checklist.battery} onChange={v => setChecklist(p => ({...p, battery: v}))}
                icon={Battery} label="Battery charged" description="Battery voltage above 13.5V during flash" />
              <ChecklistItem checked={checklist.tune_selected} onChange={() => {}}
                icon={CheckCircle} label={selectedTune ? `Tune: ${selectedTune.filename}` : "No tune selected"} description="Selected tune file to flash" />
              <ChecklistItem checked={checklist.adapter_connected} onChange={() => {}}
                icon={Cable} label={adapter ? `Adapter: ${adapter === 'thor' ? 'Thor WiFi' : 'K-DCAN USB'}` : "No adapter"} description="OBD adapter connected" />
            </div>
          </div>

          {/* Flash Button */}
          {flashDone ? (
            <Alert className="bg-emerald-500/10 border-emerald-500/30 rounded-sm">
              <CheckCircle className="h-4 w-4 text-emerald-400" />
              <AlertTitle className="text-emerald-400 font-['Outfit']">Flash Logged</AlertTitle>
              <AlertDescription className="text-zinc-300 text-sm">
                Flash event recorded for {selectedTune?.filename}. Use BTT Quickflash to complete the actual flash.
              </AlertDescription>
            </Alert>
          ) : (
            <Button disabled={!readyToFlash || flashing} onClick={handleFlash}
              className={`w-full py-4 rounded-sm font-bold text-sm tracking-wider uppercase ${readyToFlash ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'}`}
              data-testid="flash-btn">
              <Zap className="w-5 h-5 mr-2" />{flashing ? 'Logging Flash...' : readyToFlash ? 'Begin Flash' : 'Complete Checklist to Flash'}
            </Button>
          )}

          <Alert className="bg-[#050505] border-[#27272A] rounded-sm" data-testid={`flash-profile-${profile.id}`}>
            <BookOpen className="h-4 w-4 text-[#007AFF]" />
            <AlertTitle className="text-white font-['Outfit']">{profile.title}</AlertTitle>
            <AlertDescription className="text-zinc-300 text-sm space-y-3 mt-2">
              <div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#007AFF] mb-1">Recommended tools</div>
                <ul className="list-disc list-inside space-y-0.5 text-xs text-zinc-400">
                  {profile.tools.map((t) => <li key={t}>{t}</li>)}
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-[#FF5E1A] mb-1">Procedure</div>
                <ol className="list-decimal list-inside space-y-1 text-xs text-zinc-300">
                  {profile.steps.map((s) => <li key={s}>{s}</li>)}
                </ol>
              </div>
              {profile.warnings?.length > 0 && (
                <div>
                  <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.2em] text-red-400 mb-1 flex items-center gap-1.5">
                    <AlertTriangle className="w-3 h-3" />Critical warnings
                  </div>
                  <ul className="list-disc list-inside space-y-1 text-xs text-red-300/90">
                    {profile.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                </div>
              )}
              {profile.kb_url && (
                <a
                  href={profile.kb_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="flash-profile-kb-link"
                  className="inline-flex items-center gap-1 text-xs text-[#007AFF] hover:text-[#4DA3FF] font-['JetBrains_Mono'] uppercase tracking-wider mt-1"
                >
                  Platform reference →
                </a>
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}
